/**
 * MockGameApi — implements GameApi by wrapping the existing Game engine.
 * Used when VITE_WS_ENDPOINT is not set (default mock/demo mode).
 *
 * Behaviour is identical to the current app: Game runs locally with
 * fake crash points, client-side balance, and localStorage persistence.
 */

import { Game, RoundState } from '../../game/Game';
import type { Bet, Round, GameEvent } from '../../game/Game';
import {
  INITIAL_BALANCE,
  STORAGE_KEY_BALANCE,
  STORAGE_KEY_PROFILE,
  DEFAULT_USERNAME,
  DEFAULT_AVATAR,
  DEFAULT_CURRENCY,
  MAX_HISTORY,
} from '../../game/constants';
import type { GameApi, GameEventMap, Unsubscribe } from '../GameApi';
import type {
  BetPlaceRequest,
  BetCancelRequest,
  BetCashoutRequest,
  ProfileUpdateRequest,
  BetHistoryGetRequest,
  LeaderboardGetRequest,
  StreamerJoinRequest,
  StreamerLeaveRequest,
  RoundStatePayload,
  RoundBetSnapshot,
  SessionInitPayload,
  TickPayload,
  RoundCrashedPayload,
  BalanceUpdatePayload,
  BetPlaceAckPayload,
  BetCancelAckPayload,
  BetCashoutAckPayload,
  AutoCashoutPayload,
  PlayerStatsPayload,
  BetHistoryPayload,
  LeaderboardPayload,
  SessionRoundHistoryEntry,
  WsErrorPayload,
  StreamerListPayload,
  StreamerRoomUpdatePayload,
  PlayersOnlinePayload,
} from '../types';

const MOCK_PLAYER_ID = 1;
let roundCounter = 0;

export class MockGameApi implements GameApi {
  private game: Game | null = null;
  private _isConnected = false;
  private listeners = new Map<string, Set<(...args: unknown[]) => void>>();
  private balance: number;
  private playerId = MOCK_PLAYER_ID;
  private username: string;
  private avatar: string;
  private betMap = new Map<string, { betId: string; slotId: number; amount: number }>();
  private crashHistory: Array<{ roundId: number; crashPoint: number }> = [];
  private onlineTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.balance = this.loadBalance();
    const profile = this.loadProfile();
    this.username = profile.username;
    this.avatar = profile.avatar;
  }

  /** Expose the underlying Game for legacy compatibility (audio, rocket, etc.) */
  getGame(): Game {
    if (!this.game) {
      this.game = new Game();
    }
    return this.game;
  }

  get isConnected(): boolean {
    return this._isConnected;
  }

  connect(): void {
    if (this._isConnected) return;

    if (!this.game) {
      this.game = new Game();
    }
    this._isConnected = true;

    this.wireGameEvents();

    // Emit session:init on next microtask (simulates server response)
    queueMicrotask(() => {
      const snapshot = this.buildRoundSnapshot();
      const onlineSnapshot: PlayersOnlinePayload = {
        totalOnline: 100 + Math.floor(Math.random() * 900),
        updatedAt: Date.now(),
      };
      const init: SessionInitPayload = {
        playerId: this.playerId,
        username: this.username,
        avatar: this.avatar,
        balance: { amount: this.balance, currency: DEFAULT_CURRENCY },
        roundSnapshot: snapshot,
        roundHistory: this.buildSessionRoundHistory(),
        totalOnline: onlineSnapshot.totalOnline,
        serverTime: Date.now(),
        sessionTtlSec: 86400,
      };
      this.emit('session:init', init);
      this.emit('connection:status', { connected: true });
      this.emit('players:online', onlineSnapshot);
      this.startPlayersOnlineTicker();
    });
  }

  disconnect(): void {
    if (!this._isConnected) return;
    this._isConnected = false;
    if (this.game) {
      this.game.destroy();
      this.game = null;
    }
    if (this.onlineTimer) {
      clearInterval(this.onlineTimer);
      this.onlineTimer = null;
    }
    this.emit('connection:status', { connected: false });
  }

  // ── Client → Server actions ────────────────────────

  placeBet(req: BetPlaceRequest): void {
    const game = this.game;
    if (!game) {
      this.emitError('round.notActive', 'Not connected');
      return;
    }

    if (req.amount > this.balance) {
      this.emitError('insufficient.balance', 'Insufficient balance');
      return;
    }

    try {
      const bet = game.placeBet(
        req.amount,
        req.slotId,
        this.username,
        req.autoCashout ?? undefined,
        this.avatar,
      );

      this.betMap.set(bet.id, { betId: bet.id, slotId: req.slotId, amount: req.amount });

      // Ack — balance deducted immediately in mock (matches WS protocol)
      this.balance = Number((this.balance - req.amount).toFixed(2));
      if (this.balance < 0) this.balance = 0;
      this.persistBalance();

      const ack: BetPlaceAckPayload = { slotId: req.slotId, newBalance: this.balance };
      this.emit('bet:place', ack);
      this.emitBalanceUpdate('bet_placed', -req.amount);
    } catch (e) {
      this.emitError('round.notActive', (e as Error).message);
    }
  }

  cancelBet(req: BetCancelRequest): void {
    const game = this.game;
    if (!game) return;

    const entry = this.findBetBySlot(req.slotId);
    if (!entry) {
      this.emitError('bet.notFound', 'No active bet in this slot');
      return;
    }

    const success = game.cancelBet(entry.betId);
    if (!success) {
      this.emitError('round.notActive', 'Cannot cancel bet now');
      return;
    }

    // Refund
    this.balance = Number((this.balance + entry.amount).toFixed(2));
    this.persistBalance();
    this.betMap.delete(entry.betId);

    const ack: BetCancelAckPayload = { slotId: req.slotId, newBalance: this.balance };
    this.emit('bet:cancel', ack);
    this.emitBalanceUpdate('bet_cancelled', entry.amount);
  }

  cashout(req: BetCashoutRequest): void {
    const game = this.game;
    if (!game) return;

    const entry = this.findBetBySlot(req.slotId);
    if (!entry) {
      this.emitError('bet.notFound', 'No active bet in this slot');
      return;
    }

    const success = game.cashOut(entry.betId);
    if (!success) {
      this.emitError('bet.already.cashed', 'Cannot cash out now');
      return;
    }
    // Note: actual credit happens in the Game's 'cashout' event handler below
  }

  updateProfile(req: ProfileUpdateRequest): void {
    if (req.screenName != null) this.username = req.screenName;
    if (req.avatar != null) this.avatar = req.avatar;
    try {
      localStorage.setItem(STORAGE_KEY_PROFILE, JSON.stringify({
        username: this.username,
        avatar: this.avatar,
      }));
    } catch { /* no-op */ }
  }

  getPlayerStats(): void {
    // Generate mock stats from local history
    const stats: PlayerStatsPayload = {
      totalBets: 0,
      totalWins: 0,
      winRate: 0,
      totalWagered: 0,
      totalWon: 0,
      netProfit: 0,
      highestCashout: 0,
      averageMultiplier: 0,
      currentStreak: 0,
      bestStreak: 0,
    };
    queueMicrotask(() => this.emit('player:stats', stats));
  }

  getBetHistory(_req?: BetHistoryGetRequest): void {
    const payload: BetHistoryPayload = {
      bets: [],
      total: 0,
      limit: _req?.limit ?? 20,
      offset: _req?.offset ?? 0,
    };
    queueMicrotask(() => this.emit('player:betHistory:data', payload));
  }

  getLeaderboard(_req?: LeaderboardGetRequest): void {
    const payload: LeaderboardPayload = {
      type: _req?.type ?? 'earnings',
      period: _req?.period ?? 'all_time',
      entries: [],
      playerRank: null,
      playerValue: null,
    };
    queueMicrotask(() => this.emit('leaderboard:data', payload));
  }

  joinStreamerRoom(_req: StreamerJoinRequest): void {
    const payload: StreamerRoomUpdatePayload = {
      streamerId: _req.streamerId,
      displayName: _req.streamerId,
      avatar: null,
      brandColor: null,
      status: 'LIVE',
      playersInRoom: 1,
      sampleBets: [],
    };
    queueMicrotask(() => this.emit('streamer:roomUpdate', payload));
  }

  leaveStreamerRoom(_req?: StreamerLeaveRequest): void {
    // No-op in mock
  }

  getStreamerList(): void {
    const payload: StreamerListPayload = { streamers: [] };
    queueMicrotask(() => this.emit('streamer:list', payload));
  }

  // ── Event subscription ─────────────────────────────

  on<K extends keyof GameEventMap>(event: K, handler: GameEventMap[K]): Unsubscribe {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    const set = this.listeners.get(event)!;
    set.add(handler as (...args: unknown[]) => void);
    return () => { set.delete(handler as (...args: unknown[]) => void); };
  }

  // ── Internals ──────────────────────────────────────

  private emit<K extends keyof GameEventMap>(
    event: K,
    ...args: Parameters<GameEventMap[K]>
  ): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const handler of set) {
      try {
        (handler as (...a: unknown[]) => void)(...args);
      } catch (e) {
        console.error(`[MockGameApi] Error in handler for '${event}':`, e);
      }
    }
  }

  private emitError(code: string, message: string): void {
    const payload: WsErrorPayload = { code, message };
    this.emit('error', payload);
  }

  private emitBalanceUpdate(reason: string, delta: number): void {
    const payload: BalanceUpdatePayload = {
      amount: this.balance,
      currency: DEFAULT_CURRENCY,
      reason,
      delta,
      refId: null,
    };
    this.emit('balance:update', payload);
  }

  private emitPlayersOnline(): void {
    const payload: PlayersOnlinePayload = {
      totalOnline: 100 + Math.floor(Math.random() * 900),
      updatedAt: Date.now(),
    };
    this.emit('players:online', payload);
  }

  private startPlayersOnlineTicker(): void {
    if (this.onlineTimer) {
      clearInterval(this.onlineTimer);
    }
    this.onlineTimer = setInterval(() => {
      if (!this._isConnected) return;
      this.emitPlayersOnline();
    }, 30_000);
  }

  private wireGameEvents(): void {
    const game = this.game!;

    game.on('stateChange', (e: GameEvent<'stateChange'>) => {
      const state = e.data.state;

      if (state === RoundState.RUNNING) {
        // When round starts running, activate bets
        // The balance was already deducted on placeBet()
      }

      const snapshot = this.buildRoundSnapshot();
      if (snapshot) {
        this.emit('round:state', snapshot);
      }
    });

    game.on('multiplierUpdate', (e: GameEvent<'multiplierUpdate'>) => {
      if (!this._isConnected) return;
      const round = game.getCurrentRound();
      const elapsed = round ? Date.now() - round.startTime : 0;
      const tick: TickPayload = {
        multiplier: e.data.multiplier,
        elapsedMs: elapsed,
      };
      this.emit('tick', tick);
    });

    game.on('crash', (e: GameEvent<'crash'>) => {
      const round = e.data.round;
      roundCounter++;

      // All active bets are lost
      for (const [betId, entry] of this.betMap) {
        // Check if bet was already cashed out
        const gameBet = round.bets.find((b: Bet) => b.id === betId);
        if (gameBet && !gameBet.cashoutMultiplier) {
          // Lost bet — no refund needed, balance already deducted
        }
        this.betMap.delete(betId);
      }

      this.crashHistory.unshift({
        roundId: roundCounter,
        crashPoint: round.crashPoint,
      });
      if (this.crashHistory.length > MAX_HISTORY) this.crashHistory.pop();

      const crashed: RoundCrashedPayload = {
        roundId: roundCounter,
        roundNumber: roundCounter,
        crashPoint: round.crashPoint,
        serverSeed: round.serverSeed,
        serverSeedHash: round.serverSeed, // mock: same as seed
        clientSeed: round.clientSeed,
        nonce: roundCounter,
        timestamp: Date.now(),
      };
      this.emit('round:crashed', crashed);
    });

    game.on('cashout', (e: GameEvent<'cashout'>) => {
      const { bet, winAmount, multiplier, auto } = e.data;
      const entry = this.betMap.get(bet.id);
      const slotId = entry?.slotId ?? bet.slotId;

      // Credit
      this.balance = Number((this.balance + winAmount).toFixed(2));
      this.persistBalance();

      if (auto) {
        const payload: AutoCashoutPayload = {
          betId: bet.id,
          slotId,
          multiplier,
          winAmount,
        };
        this.emit('auto:cashout', payload);
      } else {
        const ack: BetCashoutAckPayload = { slotId, newBalance: this.balance };
        this.emit('bet:cashout', ack);
      }

      this.emitBalanceUpdate(auto ? 'auto_cashout' : 'cashout', winAmount);
      this.betMap.delete(bet.id);
    });
  }

  private buildRoundSnapshot(): RoundStatePayload | null {
    const game = this.game;
    if (!game) return null;

    const round = game.getCurrentRound();
    if (!round) return null;

    const state = game.getCurrentState();
    const mapPhase = (s: RoundState): 'BET_TIME' | 'RUNNING' | 'CRASH' => {
      if (s === RoundState.ROUND_START) return 'RUNNING';
      return s as 'BET_TIME' | 'RUNNING' | 'CRASH';
    };

    const bets: RoundBetSnapshot[] = round.bets.map((b: Bet) => ({
      betId: b.id,
      playerId: this.playerId,
      username: b.username,
      avatar: b.avatar ?? null,
      amount: b.amount,
      slotId: b.slotId,
      state: b.cashoutMultiplier ? 'CASHED' : b.isActive ? 'PENDING' : 'LOST',
      cashoutMultiplier: b.cashoutMultiplier ?? null,
      winAmount: b.cashoutMultiplier ? b.amount * b.cashoutMultiplier : null,
      isAuto: !!b.targetCashoutMultiplier && b.cashoutMultiplier === b.targetCashoutMultiplier,
    }));

    return {
      roundId: roundCounter || 1,
      state: mapPhase(state),
      roundNumber: game.getRoundNumber(),
      startsAt: state === RoundState.BET_TIME ? round.startTime + 5000 : null,
      elapsed: state === RoundState.RUNNING ? Date.now() - round.startTime : null,
      crashPoint: state === RoundState.CRASH ? round.crashPoint : null,
      multiplier: game.getCurrentMultiplier(),
      serverSeedHash: round.serverSeed,
      clientSeed: round.clientSeed,
      nonce: roundCounter || null,
      bets,
    };
  }

  private buildSessionRoundHistory(): SessionRoundHistoryEntry[] {
    return this.crashHistory.slice(0, MAX_HISTORY).map((item, index) => ({
      roundId: item.roundId,
      roundNumber: item.roundId,
      crashPoint: item.crashPoint,
      timestamp: Date.now() - (index * 1000),
      serverSeed: '',
      serverSeedHash: '',
      clientSeed: null,
      nonce: item.roundId,
      totalBets: 0,
      totalVolume: 0,
    }));
  }

  private findBetBySlot(slotId: number): { betId: string; slotId: number; amount: number } | undefined {
    for (const [, entry] of this.betMap) {
      if (entry.slotId === slotId) return entry;
    }
    return undefined;
  }

  private loadBalance(): number {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_BALANCE);
      if (stored) return Number(stored);
    } catch { /* no-op */ }
    return INITIAL_BALANCE;
  }

  private persistBalance(): void {
    try {
      localStorage.setItem(STORAGE_KEY_BALANCE, String(this.balance));
    } catch { /* no-op */ }
  }

  private loadProfile(): { username: string; avatar: string } {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_PROFILE);
      if (raw) {
        const p = JSON.parse(raw);
        return {
          username: (p?.username ? String(p.username).trim() : '') || DEFAULT_USERNAME,
          avatar: p?.avatar ? String(p.avatar) : DEFAULT_AVATAR,
        };
      }
    } catch { /* no-op */ }
    return { username: DEFAULT_USERNAME, avatar: DEFAULT_AVATAR };
  }
}
